import { useEffect, useCallback, useRef } from 'react';
import { logger } from '../services/loggingService';

const TAB_HEARTBEAT_KEY = 'cc_tab_heartbeat';
const DATA_CHANGE_KEY = 'cc_data_changed';
const HEARTBEAT_INTERVAL = 2000;
const TAB_TIMEOUT = 5000;

interface MultiTabSyncOptions {
  onSettingsChanged?: (key: string, newValue: string | null) => void;
  onDataChanged?: (source: 'local' | 'drive') => void;
  onOtherTabActive?: () => void;
}

const generateTabId = () => `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export function useMultiTabSync(options: MultiTabSyncOptions = {}) {
  const tabIdRef = useRef<string>(generateTabId());
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isOtherTabActive = useCallback((): boolean => {
    try {
      const lastHeartbeat = localStorage.getItem(TAB_HEARTBEAT_KEY);
      if (!lastHeartbeat) return false;
      
      const { tabId, timestamp } = JSON.parse(lastHeartbeat);
      const isRecent = Date.now() - timestamp < TAB_TIMEOUT;
      const isOtherTab = tabId !== tabIdRef.current;
      
      return isRecent && isOtherTab;
    } catch {
      return false;
    }
  }, []);

  const sendHeartbeat = useCallback(() => {
    try {
      localStorage.setItem(TAB_HEARTBEAT_KEY, JSON.stringify({
        tabId: tabIdRef.current,
        timestamp: Date.now()
      }));
    } catch {
    }
  }, []);

  const notifyDataChange = useCallback((source: 'local' | 'drive') => {
    try {
      localStorage.setItem(DATA_CHANGE_KEY, JSON.stringify({
        tabId: tabIdRef.current,
        source,
        timestamp: Date.now()
      }));
    } catch {
    }
  }, []);

  useEffect(() => {
    sendHeartbeat();
    heartbeatTimerRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    const handleStorageChange = (event: StorageEvent) => {
      if (!event.key) return;

      if (event.key === TAB_HEARTBEAT_KEY && event.newValue) {
        try {
          const { tabId } = JSON.parse(event.newValue);
          if (tabId !== tabIdRef.current) {
            options.onOtherTabActive?.();
          }
        } catch {}
      }

      if (event.key === DATA_CHANGE_KEY && event.newValue) {
        try {
          const { tabId, source } = JSON.parse(event.newValue);
          if (tabId !== tabIdRef.current) {
            logger.info('System', 'Data changed in another tab', { source });
            options.onDataChanged?.(source);
          }
        } catch {}
      }

      const settingsKeys = [
        'cc_color_palette',
        'cc_text_scale', 
        'cc_text_clarity',
        'cc_tile_edge_colors',
        'cc_logging_config',
        'cc_local_voice_config'
      ];
      
      if (settingsKeys.includes(event.key)) {
        logger.debug('System', 'Settings changed in another tab', { key: event.key });
        options.onSettingsChanged?.(event.key, event.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    logger.info('System', 'Multi-tab sync initialized', { tabId: tabIdRef.current });

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
      }
    };
  }, [options.onSettingsChanged, options.onDataChanged, options.onOtherTabActive, sendHeartbeat]);

  return {
    tabId: tabIdRef.current,
    isOtherTabActive,
    notifyDataChange
  };
}
